import { Button } from '@framework/ui/atoms/Button';
import { useCallback, useState } from 'react';
import { ApplyLicenseModal } from './ApplyLicenseModal';
import type { GroupEntity } from '@group/domain';
import { ApplyLicenseThanksModal } from './ApplyLicenseThanksModal';

type Props = {
  group: GroupEntity;
};

export type License = {
  kind: string;
  number: number;
  editable: boolean;
  monthlyFee: number;
};

const STANDARD_LICENSE: License = {
  kind: 'スタンダード',
  number: 1,
  editable: true,
  monthlyFee: 2000,
};

const BULK_25_LICENSE: License = {
  kind: 'バルクライセンス（25ユーザまで）',
  number: 25,
  editable: false,
  monthlyFee: 30000,
};

const BULK_50_LICENSE: License = {
  kind: 'バルクライセンス（50ユーザまで）',
  number: 50,
  editable: false,
  monthlyFee: 50000,
};

const BULK_100_LICENSE: License = {
  kind: 'バルクライセンス（100ユーザまで）',
  number: 100,
  editable: false,
  monthlyFee: 80000,
};

export const LicenseDescriptionPanel: React.FC<Props> = ({ group }: Props) => {
  const [showModal, setShowModal] = useState(false);
  const [showThanks, setShowThanks] = useState<boolean>(false);

  const [license, setLicense] = useState<License>(STANDARD_LICENSE);

  const handleApply = (selectedLicense: License) => {
    setShowModal(true);
    setLicense(selectedLicense);
  };

  const onApplyCompleted = useCallback(() => {
    setShowModal(false);
    setShowThanks(true);
  }, []);

  const handleCloseModal = useCallback(() => setShowModal(false), []);
  const handleCloseThanksModal = useCallback(() => setShowThanks(false), []);

  return (
    <div className="mr-40 mt-10">
      <div className="flex w-full flex-row justify-center">
        <div className="w-1/4 rounded-lg border-2" style={{ height: '400px' }}>
          <div className="rounded-t-lg bg-brand p-4 text-xl font-bold text-white" style={{ height: '70px' }}>
            スタンダードライセンス
          </div>
          <div className="bg-brand p-4 text-white" style={{ height: '80px' }}>
            個人や小規模の企業の方向け
          </div>
          <div>
            <div className="my-4 flex flex-row" style={{ height: '200px' }}>
              <div className="w-full">
                <div className="mx-7 mt-5">1ユーザ/月</div>
                <div className="mx-7 text-xl font-bold">2,000円</div>
                <div className="mx-7 mt-14">
                  <Button className="w-full" color="brand" onClick={() => handleApply(STANDARD_LICENSE)}>
                    申し込み
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="ml-4 w-3/4 rounded-lg border-2" style={{ height: '400px' }}>
          <div className="rounded-t-lg bg-gray-700 p-4 text-xl font-bold text-white" style={{ height: '70px' }}>
            バルクライセンス
          </div>
          <div className="bg-gray-700 p-4 align-top text-white" style={{ height: '80px' }}>
            中規模以上の企業の方向け
            <br />
            15ユーザ以上での利用でお得
          </div>
          <div>
            <div className="my-4 flex flex-row" style={{ height: '200px' }}>
              <div className="w-1/3 border-r-2">
                <div className="mx-7 mt-5">25ユーザまで</div>
                <div className="mx-7 text-xl font-bold">3万円/月</div>
                <div className="mx-7 mt-14">
                  <Button className="w-full" color="brand" onClick={() => handleApply(BULK_25_LICENSE)}>
                    申し込み
                  </Button>
                </div>
              </div>
              <div className="w-1/3 border-r-2">
                <div className="mx-7 mt-5">50ユーザまで</div>
                <div className="mx-7 text-xl font-bold">5万円/月</div>
                <div className="mx-7 mt-14">
                  <Button className="w-full" color="brand" onClick={() => handleApply(BULK_50_LICENSE)}>
                    申し込み
                  </Button>
                </div>
              </div>
              <div className="w-1/3">
                <div className="mx-7 mt-5">100ユーザまで</div>
                <div className="mx-7 text-xl font-bold">8万円/月</div>
                <div className="mx-7 mt-14">
                  <Button className="w-full" color="brand" onClick={() => handleApply(BULK_100_LICENSE)}>
                    申し込み
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="ml-4 mt-10 text-sm">
        <ul>
          <li>※ 表示価格は全て税抜き価格です。</li>
          <li>※ ご入力いただいたメールアドレスに請求書を送付いたします。</li>
          <li>
            ※ 100ユーザ以上での利用を検討中の方は、
            <a
              href="https://balus-help.levii.co.jp/hc/ja/requests/new"
              className="text-blue-700 underline hover:no-underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              こちら
            </a>
            までお問い合わせください。
          </li>
        </ul>
      </div>
      <ApplyLicenseModal
        isOpen={showModal}
        onApplyCompleted={onApplyCompleted}
        onClose={handleCloseModal}
        group={group}
        license={license}
      />
      <ApplyLicenseThanksModal isOpen={showThanks} onClose={handleCloseThanksModal} />
    </div>
  );
};
